import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CompanyCalendarEvent,
  fetchCompanyCalendarEvents,
} from '../../api/calendarEvent';
import {
  CertificateCalendarItem,
  fetchCertificateCalendar,
} from '../../api/certificate';
import { fetchContractCalendar, ContractCalendarItem } from '../../api/contract';
import { fetchLeaveCalendar, LeaveCalendarItem } from '../../api/leaveRequest';
import {
  fetchEquipmentCalendar,
  EquipmentCalendarItem,
} from '../../api/manufacturingEquipment';
import { fetchGateLogCalendar } from '../../api/gateLog';
import { useCompanyStore } from '../../store/companyStore';
import { CalendarEvent, CalendarEventType } from '../../types/dashboard';
import { CompanyGateLog, formatGateLogTime, gateLogTypeLabel } from '../../types/gateLog';
import { halfDayPeriodLabel, leaveTypeLabel } from '../../types/leaveRequest';
import CalendarEventModal from './CalendarEventModal';
import GateLogModal from './GateLogModal';
import './CalendarWidget.css';

import { reportError } from '../../utils/notify';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const EVENT_CLASS: Record<CalendarEventType, string> = {
  meeting: 'is-meeting',
  dinner: 'is-dinner',
  leave: 'is-leave',
  training: 'is-training',
  weekend: 'is-weekend',
  external: 'is-external',
  trip: 'is-trip',
  calibration: 'is-calibration',
  contract: 'is-contract',
  certificate: 'is-certificate',
  rental: 'is-rental',
  visit: 'is-visit',
  parcel_in: 'is-parcel-in',
  parcel_out: 'is-parcel-out',
};

const EQUIP_KIND_LABEL: Record<string, string> = {
  CALIB: '교정',
  CALIB_EXPIRE: '교정만료',
  RENTAL_START: '대여시작',
  RENTAL_END: '대여만료',
};

const CONTRACT_KIND_LABEL: Record<string, string> = {
  CONTRACT_START: '계약시작',
  DELIVERY_DUE: '납품예정',
  CONTRACT_END: '계약종료',
};

const CERT_KIND_LABEL: Record<string, string> = {
  CERT_WARN: '인증만료임박',
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function toKey(year: number, month: number, day: number) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function eachDateInclusive(start: string, end: string): string[] {
  const dates: string[] = [];
  let cur = start.slice(0, 10);
  const last = end.slice(0, 10);
  if (!cur || !last || last < cur) return dates;
  while (cur <= last) {
    dates.push(cur);
    cur = addDays(cur, 1);
  }
  return dates;
}

function leaveCalendarType(leaveType?: string | null): CalendarEventType {
  return (leaveType || '').toUpperCase() === 'TRIP' ? 'trip' : 'leave';
}

function leaveCalendarLabel(item: LeaveCalendarItem): string {
  const type = leaveTypeLabel(item.leaveType);
  const who = (item.requesterName || '').trim() || '이름없음';
  if ((item.leaveType || '').toUpperCase() === 'HALF') {
    return `${type}(${halfDayPeriodLabel(item.halfDayPeriod)})_${who}`;
  }
  return `${type}_${who}`;
}

function leaveToEvents(items: LeaveCalendarItem[]): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const item of items) {
    const start = String(item.startDate || '').slice(0, 10);
    const end = String(item.endDate || item.startDate || '').slice(0, 10);
    const type = leaveCalendarType(item.leaveType);
    const label = leaveCalendarLabel(item);
    for (const date of eachDateInclusive(start, end)) {
      events.push({
        id: `leave-${item.leaveRequestId}-${date}`,
        date,
        type,
        label,
        source: 'leave',
      });
    }
  }
  return events;
}

function equipmentTypeOf(kind: string): CalendarEventType {
  return kind.startsWith('RENTAL') ? 'rental' : 'calibration';
}

function equipmentToEvents(items: EquipmentCalendarItem[]): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const item of items) {
    const date = String(item.date || '').slice(0, 10);
    if (!date) continue;
    const kind = String(item.kind || '').toUpperCase();
    const prefix = EQUIP_KIND_LABEL[kind] || kind || '설비';
    const name = (item.equipmentName || '').trim() || '설비';
    events.push({
      id: `equip-${kind}-${item.equipmentId}-${item.calibrationId ?? 'x'}-${date}`,
      date,
      type: equipmentTypeOf(kind),
      label: `${prefix}_${name}`,
      source: 'equipment',
    });
  }
  return events;
}

function contractToEvents(items: ContractCalendarItem[]): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const item of items) {
    const date = String(item.date || '').slice(0, 10);
    if (!date) continue;
    const kind = String(item.kind || '').toUpperCase();
    const prefix = CONTRACT_KIND_LABEL[kind] || kind || '계약';
    const name = (item.contractName || '').trim() || '계약';
    events.push({
      id: `contract-${kind}-${item.contractId}-${date}`,
      date,
      type: 'contract',
      label: `${prefix}_${name}`,
      source: 'contract',
    });
  }
  return events;
}

function certificateToEvents(items: CertificateCalendarItem[]): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const item of items) {
    const date = String(item.date || '').slice(0, 10);
    if (!date) continue;
    const kind = String(item.kind || '').toUpperCase();
    const prefix = CERT_KIND_LABEL[kind] || '인증만료임박';
    const name = (item.title || '').trim() || '인증서';
    const validTo = item.validTo?.slice(0, 10);
    const label = validTo ? `${prefix}_${name}(~${validTo})` : `${prefix}_${name}`;
    events.push({
      id: `cert-${kind}-${item.certificateId}-${date}`,
      date,
      type: 'certificate',
      label,
      source: 'certificate',
    });
  }
  return events;
}

function customToEvents(items: CompanyCalendarEvent[]): CalendarEvent[] {
  return items.map((item) => ({
    id: `custom-${item.eventId}`,
    date: String(item.eventDate || '').slice(0, 10),
    type: 'meeting' as const,
    label: item.title,
    source: 'custom' as const,
    eventId: item.eventId,
    content: item.content,
    createdUserName: item.createdUserName,
  }));
}

function gateTypeOf(logType?: string | null): CalendarEventType {
  if (logType === 'PARCEL_IN') return 'parcel_in';
  if (logType === 'PARCEL_OUT') return 'parcel_out';
  return 'visit';
}

function gateToEvents(items: CompanyGateLog[]): CalendarEvent[] {
  return items.map((item) => {
    const type = gateTypeOf(item.logType);
    const typeLabel = item.logTypeLabel || gateLogTypeLabel(item.logType);
    const time = formatGateLogTime(item.logTime);
    return {
      id: `gate-${item.logId}`,
      date: String(item.logDate || '').slice(0, 10),
      type,
      label: time ? `[${typeLabel} ${time}] ${item.title}` : `[${typeLabel}] ${item.title}`,
      source: 'gate' as const,
      gateLogId: item.logId,
      content:
        [item.partyName, item.contactPhone, item.trackingNo, item.memo]
          .filter(Boolean)
          .join(' · ') || null,
      createdUserName: item.createdUserName,
    };
  });
}

function monthRange(year: number, month: number) {
  const first = new Date(year, month - 1, 1);
  const startPad = first.getDay();
  const rangeStart = new Date(year, month - 1, 1 - startPad);
  const daysInMonth = new Date(year, month, 0).getDate();
  const cellsBeforeEnd = startPad + daysInMonth;
  const endPad = (7 - (cellsBeforeEnd % 7)) % 7;
  const rangeEnd = new Date(year, month - 1, daysInMonth + endPad);
  return {
    from: toKey(rangeStart.getFullYear(), rangeStart.getMonth() + 1, rangeStart.getDate()),
    to: toKey(rangeEnd.getFullYear(), rangeEnd.getMonth() + 1, rangeEnd.getDate()),
  };
}

type ModalState =
  | { kind: 'event'; mode: 'create'; date: string }
  | { kind: 'event'; mode: 'edit'; date: string; event: CompanyCalendarEvent }
  | { kind: 'gate'; mode: 'create'; date: string }
  | { kind: 'gate'; mode: 'edit'; date: string; log: CompanyGateLog }
  | null;

function CalendarWidget() {
  const companyId = useCompanyStore((s) => s.companyId);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [customEvents, setCustomEvents] = useState<CompanyCalendarEvent[]>([]);
  const [gateLogs, setGateLogs] = useState<CompanyGateLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);

  const load = useCallback(async () => {
    if (!companyId) {
      setEvents([]);
      setCustomEvents([]);
      setGateLogs([]);
      return;
    }
    const { from, to } = monthRange(year, month);
    try {
      setLoading(true);
      setError('');
      const [leaveRows, equipRows, contractRows, certRows, customRows, gateRows] =
        await Promise.all([
          fetchLeaveCalendar(from, to),
          fetchEquipmentCalendar(from, to),
          fetchContractCalendar(from, to),
          fetchCertificateCalendar(from, to),
          fetchCompanyCalendarEvents(from, to),
          fetchGateLogCalendar(from, to),
        ]);
      setCustomEvents(customRows);
      setGateLogs(gateRows);
      setEvents([
        ...customToEvents(customRows),
        ...leaveToEvents(leaveRows),
        ...equipmentToEvents(equipRows),
        ...contractToEvents(contractRows),
        ...certificateToEvents(certRows),
        ...gateToEvents(gateRows),
      ]);
    } catch {
      reportError(setError, '일정을 불러오지 못했습니다.');
      setEvents([]);
      setCustomEvents([]);
      setGateLogs([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((ev) => {
      const list = map.get(ev.date) ?? [];
      list.push(ev);
      map.set(ev.date, list);
    });
    return map;
  }, [events]);

  const cells = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const prevDays = new Date(year, month - 1, 0).getDate();
    const now = new Date();
    const todayKey = toKey(now.getFullYear(), now.getMonth() + 1, now.getDate());
    const result: Array<{
      day: number;
      inMonth: boolean;
      dateKey: string;
      isToday: boolean;
    }> = [];

    for (let i = firstDay - 1; i >= 0; i -= 1) {
      const day = prevDays - i;
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const dateKey = toKey(prevYear, prevMonth, day);
      result.push({ day, inMonth: false, dateKey, isToday: dateKey === todayKey });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateKey = toKey(year, month, day);
      result.push({ day, inMonth: true, dateKey, isToday: dateKey === todayKey });
    }

    const remain = 42 - result.length;
    for (let day = 1; day <= remain; day += 1) {
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      const dateKey = toKey(nextYear, nextMonth, day);
      result.push({ day, inMonth: false, dateKey, isToday: dateKey === todayKey });
    }

    return result;
  }, [year, month]);

  const selectedEvents = selectedKey ? eventsByDate.get(selectedKey) ?? [] : [];

  const goPrev = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goNext = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const goToday = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
    setSelectedKey(toKey(now.getFullYear(), now.getMonth() + 1, now.getDate()));
  };

  const openCreate = (dateKey: string) => {
    setSelectedKey(dateKey);
    setModal({ kind: 'event', mode: 'create', date: dateKey });
  };

  const openCreateGate = (dateKey: string) => {
    setSelectedKey(dateKey);
    setModal({ kind: 'gate', mode: 'create', date: dateKey });
  };

  const openEdit = (ev: CalendarEvent) => {
    if (ev.source === 'gate' && ev.gateLogId != null) {
      const found = gateLogs.find((row) => row.logId === ev.gateLogId);
      if (!found) return;
      setModal({ kind: 'gate', mode: 'edit', date: found.logDate.slice(0, 10), log: found });
      return;
    }
    if (ev.source !== 'custom' || ev.eventId == null) return;
    const found = customEvents.find((row) => row.eventId === ev.eventId);
    if (!found) return;
    setModal({ kind: 'event', mode: 'edit', date: found.eventDate.slice(0, 10), event: found });
  };

  const selectedLabel = selectedKey
    ? (() => {
        const [y, m, d] = selectedKey.split('-').map(Number);
        const wd = WEEKDAYS[new Date(y, m - 1, d).getDay()];
        return `${y}년 ${m}월 ${d}일 (${wd})`;
      })()
    : '';

  return (
    <section className="ec-widget ec-calendar">
      <div className="ec-widget__header">
        <div className="ec-calendar__title">
          <div className="ec-calendar__heading">
            <strong>일정관리</strong>
            <span className="ec-calendar__sub">더블클릭으로 등록</span>
          </div>
          <div className="ec-calendar__month">
            <button type="button" className="ec-nav-btn" onClick={goPrev} aria-label="이전 달">
              ‹
            </button>
            <strong>
              {year}.{pad(month)}
            </strong>
            <button type="button" className="ec-nav-btn" onClick={goNext} aria-label="다음 달">
              ›
            </button>
          </div>
        </div>
        <div className="ec-widget__actions">
          <button type="button" className="ec-tool-btn is-text" title="오늘" onClick={goToday}>
            오늘
          </button>
          <button
            type="button"
            className="ec-tool-btn"
            title="새로고침"
            onClick={() => void load()}
            disabled={loading}
          >
            ↻
          </button>
        </div>
      </div>

      {error ? <p className="ec-calendar__error">{error}</p> : null}

      <div className="ec-calendar__weekdays">
        {WEEKDAYS.map((d) => (
          <div key={d} className="ec-calendar__weekday">
            {d}
          </div>
        ))}
      </div>

      <div className="ec-calendar__grid">
        {cells.map((cell) => {
          const dayEvents = eventsByDate.get(cell.dateKey) ?? [];
          const first = dayEvents[0];
          const more = Math.max(0, dayEvents.length - 1);
          return (
            <button
              key={cell.dateKey}
              type="button"
              className={`ec-calendar__cell${cell.inMonth ? '' : ' is-out'}${
                cell.isToday ? ' is-today' : ''
              }${selectedKey === cell.dateKey ? ' is-selected' : ''}`}
              onClick={() => setSelectedKey(cell.dateKey)}
              onDoubleClick={() => openCreate(cell.dateKey)}
              title="더블클릭하여 일정 등록"
            >
              <span className="ec-calendar__day">{cell.day}</span>
              <div className="ec-calendar__events">
                {first ? (
                  <span
                    className={`ec-event-badge ${EVENT_CLASS[first.type]}`}
                    title={more > 0 ? `${first.label} 외 ${more}개` : first.label}
                  >
                    {first.label}
                  </span>
                ) : null}
                {more > 0 ? <span className="ec-calendar__more">외 {more}개</span> : null}
              </div>
            </button>
          );
        })}
      </div>

      <div className="ec-calendar__detail">
        {loading ? (
          <p>불러오는 중...</p>
        ) : selectedKey ? (
          <>
            <div className="ec-calendar__detail-head">
              <strong>{selectedLabel}</strong>
              <div className="ec-calendar__detail-actions">
                <button type="button" className="ec-calendar__add-btn" onClick={() => openCreate(selectedKey)}>
                  + 일정
                </button>
                <button
                  type="button"
                  className="ec-calendar__add-btn is-gate"
                  onClick={() => openCreateGate(selectedKey)}
                >
                  + 출입·수발신
                </button>
              </div>
            </div>
            {selectedEvents.length === 0 ? (
              <p>등록된 일정이 없습니다. 「일정」 또는 「출입·수발신」으로 추가하세요.</p>
            ) : (
              <ul>
                {selectedEvents.map((ev) => (
                  <li
                    key={ev.id}
                    className={
                      ev.source === 'custom' || ev.source === 'gate' ? 'is-custom' : undefined
                    }
                  >
                    <span className={`ec-event-dot ${EVENT_CLASS[ev.type]}`} />
                    <div className="ec-calendar__detail-item">
                      <div className="ec-calendar__detail-main">
                        <span className="ec-calendar__detail-title">{ev.label}</span>
                        {ev.content ? (
                          <span className="ec-calendar__detail-memo">{ev.content}</span>
                        ) : null}
                        {(ev.source === 'custom' || ev.source === 'gate') && ev.createdUserName ? (
                          <span className="ec-calendar__detail-meta">{ev.createdUserName}</span>
                        ) : null}
                      </div>
                      {ev.source === 'custom' || ev.source === 'gate' ? (
                        <button
                          type="button"
                          className="ec-calendar__edit-btn"
                          onClick={() => openEdit(ev)}
                        >
                          수정
                        </button>
                      ) : (
                        <span className="ec-calendar__readonly">자동</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <p>
            날짜를 선택해 일정·출입·수발신을 등록하세요. 휴가·교정·대여·계약·인증서 일정도 함께
            표시됩니다.
          </p>
        )}
      </div>

      {modal?.kind === 'event' ? (
        <CalendarEventModal
          mode={modal.mode}
          date={modal.date}
          event={modal.mode === 'edit' ? modal.event : null}
          onClose={() => setModal(null)}
          onSaved={() => void load()}
        />
      ) : null}
      {modal?.kind === 'gate' ? (
        <GateLogModal
          mode={modal.mode}
          date={modal.date}
          log={modal.mode === 'edit' ? modal.log : null}
          onClose={() => setModal(null)}
          onSaved={() => void load()}
        />
      ) : null}
    </section>
  );
}

export default CalendarWidget;
